import { SupportedArtworkSizes } from 'constants/supportedArtworksSizes';
import React from 'react';
import { Box, Group, Skeleton, MusicNote } from '@landr/maestro';
import CommonArtwork from 'components/CommonUi/Artwork/Artwork';
import { ArtworkSize } from 'types/artworkSize';
import { Artwork as ArtworkType } from './types';

const MainImageWidth = 58;
const MainRectangleImageWidth = 72;
const BackgroundImageGap = 16;
const BackgroundImageWidth = (100 - BackgroundImageGap) / 2;
const BackgroundRectangleImageGap = 33;

const Artworks = ({
    artworks,
    imageSize = 'default',
}: {
    artworks?: [ArtworkType] | [ArtworkType, ArtworkType, ArtworkType];
    imageSize?: ArtworkSize;
}) => {
    let main: ArtworkType | undefined;
    let left: ArtworkType | undefined;
    let right: ArtworkType | undefined;

    switch (artworks?.length) {
        case 1:
            main = artworks[0];
            left = artworks[0];
            right = artworks[0];
            break;
        case 3:
            main = artworks[0];
            left = artworks[1];
            right = artworks[2];
            break;
        default:
            main = undefined;
            left = undefined;
            right = undefined;
    }

    const [imageWidth, imageHeight] = SupportedArtworkSizes[imageSize];
    const isRectangle = imageWidth !== imageHeight;

    return (
        <Group.Item
            textAlign="center"
            position="relative"
            width="100%"
            userSelect="none"
        >
            <Group
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                display="flex"
                opacity="0.24"
                justifyContent="center"
                alignItems="center"
                style={{ gap: `${BackgroundImageGap}%` }}
            >
                <Group.Item
                    position="relative"
                    display="flex-item"
                    flexGrow="1"
                    textAlign="right"
                    borderRadius="lg"
                    overflow="hidden"
                    style={{
                        paddingBottom: isRectangle
                            ? `${BackgroundRectangleImageGap}%`
                            : `${BackgroundImageWidth}%`,
                    }}
                >
                    {!left && (
                        <Skeleton
                            aria-busy="true"
                            position="absolute"
                            inset="0"
                            paddingBottom="100%"
                        />
                    )}
                    {left && (
                        <Box position="absolute" inset="0">
                            <CommonArtwork
                                alt=""
                                imageUrl={left.artworkUrl}
                                fallbackIcon={MusicNote}
                                isExclusive={left.isExclusive}
                                imageSize={imageSize}
                                loading="eager"
                            />
                        </Box>
                    )}
                </Group.Item>
                <Group.Item
                    display="flex-item"
                    flexGrow="1"
                    paddingBottom={
                        isRectangle
                            ? `${BackgroundRectangleImageGap}%`
                            : `${BackgroundImageWidth}%`
                    }
                    borderRadius="lg"
                    overflow="hidden"
                    position="relative"
                >
                    {!right && (
                        <Skeleton
                            aria-busy="true"
                            position="absolute"
                            inset="0"
                            paddingBottom="100%"
                        />
                    )}
                    {right && (
                        <Box position="absolute" inset="0">
                            <CommonArtwork
                                alt=""
                                imageUrl={right.artworkUrl}
                                fallbackIcon={MusicNote}
                                isExclusive={right.isExclusive}
                                imageSize={imageSize}
                                loading="eager"
                            />
                        </Box>
                    )}
                </Group.Item>
            </Group>
            <Box
                position="relative"
                margin="0 auto"
                width={
                    isRectangle
                        ? `${MainRectangleImageWidth}%`
                        : `${MainImageWidth}%`
                }
                paddingTop={`${MainImageWidth}%`}
                borderRadius="lg"
                overflow="hidden"
                boxShadow="lg"
            >
                {!main && (
                    <Skeleton
                        aria-busy="true"
                        position="absolute"
                        inset="0"
                        paddingBottom="100%"
                    />
                )}
                {main && (
                    <Box position="absolute" inset="0">
                        <CommonArtwork
                            alt=""
                            imageUrl={main.artworkUrl}
                            fallbackIcon={MusicNote}
                            isExclusive={main.isExclusive}
                            imageSize={imageSize}
                            loading="eager"
                        />
                    </Box>
                )}
            </Box>
        </Group.Item>
    );
};

export default Artworks;
